import { AppAPI, TimeSeriesCollectionRawData, EntityMetricsDefinition, NumericMetricInfo, TagMetricInfo, MetricInfo, SortOrder }
    from '@moderepo/mode-apis';
import { BaseEntityField, capitalizeAll, GenericMessageBox, setLocalStorageItem, useLocalStorage }
    from '@moderepo/mode-app-components';
import { DialogResponse, DialogResponseCode, DialogType, modeUIContext, setIsEditingForm, showDialog } from '@moderepo/mode-ui-state';
import clsx from 'clsx';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { EntityUIComponentCollection, EntityUIComponentProps, filterMetricsByMetricsExclusionCustomSettings,
    filterMetricsDefinitionsByMetricsExclusionCustomSettings, getMetricsDefinitionMetricsId, isKPIMetric,
    isMetricsDefinitionAndMetricsExclusionCustomSettings, MetricsDefinitionsAndMetricsExclusionCustomSettings,
    useSelectTranslatedEntityClasses } from '..';
import { EntityMetricsValuesViewComp, EntityMetricsValuesViewCompProps, HiddenMetricsByMetricDefId } from '../../../components';
import { useRitzTranslation } from '../../../utils/customHooks';
import { useSelectEntity, useSelectProject } from '../../../utils/dataStateHooks';
import { SettingsErrorLogger } from '../../../utils/logger';
import { RitzComponentsCollection } from '../../../utils/ritzComponentsCollection';
import { isObjectLiteral, isValidSettingStringValue } from '../../../utils/utils';
import { getDateTimeDisplay } from '../../containerUtils';
import { DUMMY_VALUE_NAME } from '../../time-series/tsUtils';



/**
 * An interface to keep track of which numeric/tag metrics are displayable for EACH metric definition. Not all metrics from all metric definition
 * are displayable. The admin can exclude metrics using uiSettings therefore we need to filter out excluded metrics. Once we filtered out
 * excluded metrics, we will store the list of displayable metrics here.
 */
interface DisplayableMetrics {
    // Keep a reference to the metric definition that the numeric/tag metrics belonged to. We will need this to fetch data.
    readonly metricDefinition: EntityMetricsDefinition,

    // The array of displayable numeric metrics
    readonly numericMetrics: {
        readonly metric: NumericMetricInfo;
        value?: string | number | null | undefined;

        // Whether or not this metric is hidden which is configurable per agent
        hidden?: boolean | undefined;
    }[];

    // The array of displayable tag metrics
    readonly tagMetrics: {
        readonly metric: TagMetricInfo;
        value?: string | number | null | undefined;

        // Whether or not this metric is hidden which is configurable per agent
        hidden?: boolean | undefined;
    }[];

    /**
     * Whether or not to hide all the metrics from this metric definition. A metric definition can be displayable but hidden, these are 2
     * different settings. Whether or not a metric definition is displayable is configured from entity class' UI setting and it apply to
     * all agents/operators. However, for a displayable metric definition, the agent/operator can still hide it manually. This is per
     * agent/operator only.
     */
    hidden?: boolean | undefined;

    latestDataTimestamp?: number | undefined;
}



/**
 * The key of the local storage item we will be using to store ALL hidden metrics settings for all entities. Each entity can have custom
 * hidden metric settings but they will all be stored under this 1 LS item. This makes it easier to clean up than having 1 LS item per
 * entity. If we need to delete all these settings, we just need to delete 1 LS item. This will be the structure of this LS item
 *      localStorage: {
 *          ...,                                                // other LS items,
 *          EntityLatestMetricValueViewUIComponent: {
 *              // hidden metrics settings per entity
 *              1315::room::tyo_office: {...},              // hidden metric settings per entity
 *              1310::storage: {...}                        // hidden metric settings per entity class
 *              ...
 *          }
 *      }
 */
const LS_ENTITY_LATEST_METRIC_VALUE_VIEW_HIDDEN_METRICS = 'EntityLatestMetricValueViewUIComponent';


/**
   * Get the SUB localStorage key we need to use for storing the hiddenMetrics config for the specified entityClass and entityId. The agent can
   * select metrics they want to show/hide and we will store the config locally so that we can restore it when the agent come back to this page.
   * The agent can apply hidden metrics settings to the entire class or for 1 specific metrics. If the settings is applied to the whole class,
   * all other entities of the same class will use the same settings, EXCEPT for the entity that has specific settings.
   */
const getHiddenMetricsSubLSKey = (projectId: number, entityClass: string, entityId: string, applyToAllInstances: boolean): string => {
    // If applyToAllInstances then DON'T include entityId in the key
    return `${projectId}::${entityClass}${applyToAllInstances ? '' : `::${entityId}`}`;
};



const getMetricAttributeValue = (metric: MetricInfo, attrName: 'name' | 'displayName') => {
    if (attrName === 'displayName' && isKPIMetric(metric) && (metric as NumericMetricInfo).evaluator?.displayName) {
        // For KPI Metric, we need to get displayName from the evaluator settings if there is one.
        return (metric as NumericMetricInfo).evaluator?.displayName;
    }
    return metric[attrName];
};


/**
 * Compare 2 metrics based on the sortBy and sortOrder
 */
const compareMetrics = (
    metric1: MetricInfo, metric2: MetricInfo, sortBy: 'name' | 'displayName' | undefined, sortOrder: SortOrder | undefined,
): number => {
    if (sortBy !== undefined) {
        const value1 = (getMetricAttributeValue(metric1, sortBy) ?? '').toLowerCase();
        const value2 = (getMetricAttributeValue(metric2, sortBy) ?? '').toLowerCase();

        // We will sort everything in ASC order FIRST and then multiple the result by -1 if the sort order is DESC.
        const reverseOrderMultiplier = sortOrder === SortOrder.DESC ? -1 : 1;

        if (value1 !== undefined && value2 !== undefined) {
            if (value1 < value2) return -1 * reverseOrderMultiplier;
            if (value1 > value2) return 1 * reverseOrderMultiplier;
            return 0;
        }
        if (value1 !== undefined) return -1 * reverseOrderMultiplier;
        if (value2 !== undefined) return 1 * reverseOrderMultiplier;
    }
    return 0;
};



/**
 * Interface for EntityLatestMetricValueViewUIComponentCustomSettings's customSettings. These are the props/value that can be
 * configured for the 'EntityLatestMetricValueViewUIComponentCustomSettings' from the entityClass's uiSettings.
 */
export interface EntityLatestMetricValueViewUIComponentCustomSettings extends MetricsDefinitionsAndMetricsExclusionCustomSettings {

    // To sort the metrics in a specific order. The default is NO sorting.
    readonly sortOrder?: SortOrder | undefined

    // To sort the metrics by a specific field. The default is NO sorting.
    readonly sortBy?: 'name' | 'displayName' | undefined
}



/**
 * type guard function to check if an object is a type of EntityLatestMetricValueViewUIComponentCustomSettings
 */
const isEntityLatestMetricValueViewUIComponentCustomSettings = (
    obj: unknown,
    errorLogger?: SettingsErrorLogger | undefined,
    currentConfigPath?: readonly string[] | undefined,
): obj is EntityLatestMetricValueViewUIComponentCustomSettings => {

    const settings = obj as EntityLatestMetricValueViewUIComponentCustomSettings;
    const configPath = [...(currentConfigPath ?? [])];

    if (!isObjectLiteral(settings)) {
        return errorLogger?.logError({
            message: 'Value must be undefined or an object.',
            value  : settings,
            path   : [...configPath],
        }) ?? false;
    }

    // sortOrder is optional but if it is provided then it must be either SortOrder.ASC or SortOrder.DESC
    if (!isValidSettingStringValue(settings.sortOrder, false, false, Object.values(SortOrder))) {
        return errorLogger?.logError({
            message: `"sortOrder" must be undefined or one of these values [${Object.values(SortOrder).map((value) => {
                return `"${value}"`;
            }).join(', ')}].`,
            value: settings.sortOrder,
            path : [...configPath, 'sortOrder'],
        }) ?? false;
    }

    const sortByOptions = ['name', 'displayName'];
    if (!isValidSettingStringValue(settings.sortBy, false, false, sortByOptions)) {
        return errorLogger?.logError({
            message: `"sortBy" must be undefined or one of these values [${Object.values(sortByOptions).map((value) => {
                return `"${value}"`;
            }).join(', ')}].`,
            value: settings.sortBy,
            path : [...configPath, 'sortBy'],
        }) ?? false;
    }

    if (!isMetricsDefinitionAndMetricsExclusionCustomSettings(settings, errorLogger, [...configPath])) {
        return false;
    }

    return true;
};



export interface EntityLatestMetricValueViewUIComponentProps extends EntityUIComponentProps {
    readonly customSettings?: EntityLatestMetricValueViewUIComponentCustomSettings | undefined;
}


/**
 * This component shows an Entity metrics' latest values in tabular form.
 */
export const EntityLatestMetricValueViewUIComponent: React.FC<EntityLatestMetricValueViewUIComponentProps> = (
    props: EntityLatestMetricValueViewUIComponentProps,
) => {
    const { dispatch:uiDispatch } = useContext(modeUIContext);
    const project = useSelectProject(props.projectId);
    const { trans } = useRitzTranslation(project);
    const [isEditing, setIsEditing] = useState<boolean>();

    const selectedEntity = useSelectEntity(props.projectId, props.entityId, false);
    const entityClasses = useSelectTranslatedEntityClasses(props.projectId, trans);

    const [displayableMetricsPerMetricDef, setDisplayableMetricsPerMetricDef] = useState<readonly DisplayableMetrics[]>();

    // Use the useLocalStorage hook to get the LS item we will be using for storing the metrics hidden state
    const {
        value: globalHiddenMetricsSettings,
        setValue: setGlobalHiddenMetricsSettings,
    } = useLocalStorage(LS_ENTITY_LATEST_METRIC_VALUE_VIEW_HIDDEN_METRICS, {});

    // A LOCAL map of the metrics' hidden state grouped by metric definition ID. We will initialize it props.hiddenMetrics if it is provided
    const [hiddenMetricsByMetricDefId, setHiddenMetricsByMetricDefId] = useState<HiddenMetricsByMetricDefId>({});


    // This useEffect is used for initializing the globalHiddenMetricsSettings with settings stored in localStorage
    useEffect(() => {
        if (selectedEntity) {
            // Get the hidden metrics settings for this specific child entity from LS
            const hiddenMetricsInstanceSettings = globalHiddenMetricsSettings[getHiddenMetricsSubLSKey(
                props.projectId,
                selectedEntity.entityClass,
                selectedEntity.entityId,
                false,
            )];

            // Get the hidden metrics settings for this child entityClass from LS
            const hiddenMetricsClassSettings = globalHiddenMetricsSettings[getHiddenMetricsSubLSKey(
                props.projectId,
                selectedEntity.entityClass,
                selectedEntity.entityId,
                true,
            )];

            // hidden metrics settings per entity instance has priority so we will use it first, if not use classSettings
            setHiddenMetricsByMetricDefId(hiddenMetricsInstanceSettings ?? hiddenMetricsClassSettings ?? {});
        }
    }, [globalHiddenMetricsSettings, props.projectId, selectedEntity]);



    // Find the entityClass object that the selectedEntity belonged to
    const selectedEntityClass = useMemo(() => {
        if (selectedEntity && entityClasses) {
            return entityClasses.find((entityClass) => {
                return entityClass.entityClass === selectedEntity.entityClass;
            });
        }
        return undefined;
    }, [entityClasses, selectedEntity]);



    const getDisplayableMetrics = useCallback((): readonly DisplayableMetrics[] | undefined => {
        if (selectedEntity && selectedEntityClass) {

            const result: DisplayableMetrics[] = [];

            // filter out metricDef that we need to exclude based on the settings
            filterMetricsDefinitionsByMetricsExclusionCustomSettings(
                selectedEntityClass.metricsDefinitions ?? [], props.customSettings,
            ).forEach((metricDef) => {

                // Then for each metricDef that is NOT filtered out, compile a list of valueNames and tagNames for that
                // metricDef and add them to the result

                // NOTE: filterMetricsByMetricsExclusionCustomSettings returns an 'readonly' array so we can't call .sort function. We must
                // make a copy of the result array.
                const visibleNumericMetrics = [...filterMetricsByMetricsExclusionCustomSettings(
                    metricDef.metricsDefinitionId,
                    metricDef.numericMetrics,
                    props.customSettings,
                    true,
                )].sort((metric1, metric2) => {
                    // compare the metrics based on settings
                    return compareMetrics(metric1, metric2, props.customSettings?.sortBy, props.customSettings?.sortOrder);
                });

                const visibleTagMetrics = [...filterMetricsByMetricsExclusionCustomSettings(
                    metricDef.metricsDefinitionId,
                    metricDef.tagMetrics,
                    props.customSettings,
                    false,
                )].sort((metric1, metric2) => {
                    // compare the metrics based on settings
                    return compareMetrics(metric1, metric2, props.customSettings?.sortBy, props.customSettings?.sortOrder);
                });

                // Only add metrics from metricDef if there is at least 1 visible valueNames or tagNames
                if (visibleNumericMetrics.length > 0 || visibleTagMetrics.length > 0) {
                    result.push({
                        metricDefinition: metricDef,
                        numericMetrics  : visibleNumericMetrics.map((metric) => {
                            return {
                                metric,
                            };
                        }),
                        tagMetrics: visibleTagMetrics.map((metric) => {
                            return {
                                metric,
                            };
                        }),
                    });
                }
            });

            return result;
        }
        return undefined;
    }, [props.customSettings, selectedEntity, selectedEntityClass]);


    const fetchData = useCallback(async (): Promise<readonly DisplayableMetrics[] | undefined> => {
        const visibleMetricDefsAndMetrics = getDisplayableMetrics();
        if (selectedEntity && selectedEntityClass && visibleMetricDefsAndMetrics) {
            // Because an entity can have more than 1 metrics definitions and each metrics definition is a Collection therefore we need to
            // make multiple API calls to fetch data for all the collections. We will compile a list of API call and then dispatch them
            // in 1 promise
            const getDataAPICalls: Promise<TimeSeriesCollectionRawData>[] = [];

            const startTime = (new Date()).toISOString();

            // For each metric definition, make API call to fetch the collection data associated to that metric def
            visibleMetricDefsAndMetrics.forEach((visibleMetricDef) => {
                const collectionId = getMetricsDefinitionMetricsId(selectedEntity, visibleMetricDef.metricDefinition.metricsDefinitionId);
                const valueNames = visibleMetricDef.numericMetrics.map((metric) => {
                    return metric.metric.name;
                });
                const tagNames = visibleMetricDef.tagMetrics.map((metric) => {
                    return metric.metric.name;
                });
                getDataAPICalls.push(AppAPI.getInstance().getTimeSeriesCollectionRawData(
                    selectedEntity.homeId,
                    visibleMetricDef.metricDefinition.dataStoreConfiguration.moduleInstanceId,
                    collectionId,
                    startTime,
                    -1,
                    valueNames.length > 0 ? valueNames : [DUMMY_VALUE_NAME],
                    tagNames,
                ));
            });


            // Make all the API calls and get the responses
            const responses = await Promise.allSettled(getDataAPICalls);


            // Now get the collection data from the response and store them in this result object
            responses.forEach((response, responseIndex) => {
                if (response.status === 'fulfilled') {

                    // We requested for 1 data point for each valueNames and tagNames so we can expect the response data will contain an
                    // array of 1 data point. It is possible to be empty if the collection does not have any data
                    const data = response.value.data[0];

                    // We loop through the visibleMetricDefsAndMetrics array to create API calls so we can assume the responses will be in
                    // the same order.
                    const visibleMetricDef = visibleMetricDefsAndMetrics[responseIndex];

                    visibleMetricDef.latestDataTimestamp = data && data.length > 0 ? Date.parse(data[0]) : undefined;

                    // Get the value for numeric metrics first because they are values for 'valueNames'. NOTE: the data will start at index 1
                    // because index 0 is for the timestamp
                    (visibleMetricDef.numericMetrics).forEach((metric, index) => {
                        // eslint-disable-next-line no-param-reassign
                        metric.value = data?.[index + 1];
                    });

                    // Get the values for tag metrics starting from the index of the LAST numeric metric value. NOTE that if numeric metrics
                    // has length 0, we still make API call with at least 1 value name.
                    const numericMetricsLength = Math.max(1, visibleMetricDef.numericMetrics.length);
                    (visibleMetricDef.tagMetrics).forEach((metric, index) => {
                        // eslint-disable-next-line no-param-reassign
                        metric.value = data?.[numericMetricsLength + index + 1];
                    });
                }
            });

            return visibleMetricDefsAndMetrics;
        }

        return undefined;
    }, [selectedEntity, selectedEntityClass, getDisplayableMetrics]);


    useEffect(() => {
        fetchData().then((data) => {
            setDisplayableMetricsPerMetricDef(data);
        });
    }, [fetchData]);



    /**
     * Turn on/off edit mode LOCALLY and GLOBALLY
     * @param value - The edit mode true/false
     */
    const onChangeEditingMode = useCallback((value: boolean) => {
        // turn on/off editing mode GLOBALLY so that the system can disable all other components
        uiDispatch(setIsEditingForm(value));

        // turn on/off editing mode LOCALLY
        setIsEditing(value);
    }, [uiDispatch]);


    /**
     * end editing mode
     */
    const closeEditing = useCallback(() => {
        onChangeEditingMode(false);
    }, [onChangeEditingMode]);



    /**
     * On hidden metrics updated for one of the child entities
     */
    const onHiddenMetricsUpdated = useCallback((updatedHiddenMetrics: HiddenMetricsByMetricDefId) => {
        if (!selectedEntity) {
            return;
        }

        // Save the new metric hidden state in local storage so that we can restore it later when the user come back
        // Check with the agent if they want to apply this settings to all entities of the same class
        uiDispatch(showDialog({
            type          : DialogType.CONFIRM,
            title         : 'dialog.generic_confirm_change_entity_view_settings.title',
            message       : 'dialog.generic_confirm_change_entity_view_settings.message',
            positiveButton: 'label.yes',
            negativeButton: 'label.no',
            onClose       : async (response?: DialogResponse): Promise<void> => {
                if (response) {

                    const applyToAllInstances = response.code === DialogResponseCode.POSITIVE;

                    // get the SUB LS key which is used for storing hidden metric settings for the specified entity
                    const lsKey = getHiddenMetricsSubLSKey(
                        props.projectId,
                        selectedEntity.entityClass,
                        selectedEntity.entityId,
                        applyToAllInstances,
                    );

                    const updatedGlobalHiddenMetricsSettings = {
                        ...globalHiddenMetricsSettings,
                        [lsKey]: updatedHiddenMetrics,
                    };

                    if (applyToAllInstances) {
                        // If the user choose to apply to all instances then we need to delete the existing settings we created for the
                        // specific entityId.
                        const keyToBeDeleted = getHiddenMetricsSubLSKey(
                            props.projectId, selectedEntity.entityClass, selectedEntity.entityId, false,
                        );
                        delete updatedGlobalHiddenMetricsSettings[keyToBeDeleted];
                    }

                    // Update LOCAL state
                    setGlobalHiddenMetricsSettings(updatedGlobalHiddenMetricsSettings);

                    // Write to the localStorage
                    setLocalStorageItem(LS_ENTITY_LATEST_METRIC_VALUE_VIEW_HIDDEN_METRICS, updatedGlobalHiddenMetricsSettings);
                }
            },

        }));
    }, [globalHiddenMetricsSettings, props.projectId, selectedEntity, setGlobalHiddenMetricsSettings, uiDispatch]);



    /**
     * On hidden metrics changed
     */
    const onFormSubmit = useCallback((updatedHiddenMetrics: HiddenMetricsByMetricDefId) => {

        // Save the new metrics hidden state locally
        setHiddenMetricsByMetricDefId(updatedHiddenMetrics);

        closeEditing();

        // call 'onHiddenMetricsUpdated' to handle saving the state to LS
        onHiddenMetricsUpdated(updatedHiddenMetrics);
    }, [closeEditing, onHiddenMetricsUpdated]);



    /**
     * Create 1 field for each metric. This set of fields will be displayed in the EntityMetricsValuesViewComp component
     */
    const fieldSettings = useMemo((): EntityMetricsValuesViewCompProps['fields'] => {
        if (displayableMetricsPerMetricDef) {
            const fieldsGroups = {};

            displayableMetricsPerMetricDef.forEach((displayableMetrics) => {

                // Get the local hidden metrics settings and apply it to the fields
                const hiddenMetrics = hiddenMetricsByMetricDefId[displayableMetrics.metricDefinition.metricsDefinitionId];

                const fields: {
                    [metricName: string]: BaseEntityField<string>
                } = {};

                // Create a field for each numeric metric and add it to the map of `fields`
                displayableMetrics.numericMetrics.forEach((displayableMetricInfo) => {
                    fields[displayableMetricInfo.metric.name] = {
                        label: displayableMetricInfo.metric.evaluator?.displayName
                                ?? displayableMetricInfo.metric.displayName
                                ?? displayableMetricInfo.metric.name,
                        value: displayableMetricInfo.value !== undefined && displayableMetricInfo.value !== null
                            ? trans('label.value_unit_display', {
                                value: trans('{{value, number-format}}', { value: Number(displayableMetricInfo.value) }),
                                unit : displayableMetricInfo.metric.unit,
                            })
                            : trans('label.none'),
                        hidden: Boolean(hiddenMetrics?.[displayableMetricInfo.metric.name]),
                    };
                });

                // Create a field for each tag metric and add it to the map of `fields`
                displayableMetrics.tagMetrics.forEach((displayableMetricInfo) => {
                    fields[displayableMetricInfo.metric.name] = {
                        label: displayableMetricInfo.metric.displayName ?? displayableMetricInfo.metric.name,
                        value: displayableMetricInfo.value !== undefined && displayableMetricInfo.value !== null
                            ? displayableMetricInfo.value.toString()
                            : trans('label.none'),
                        hidden: Boolean(hiddenMetrics?.[displayableMetricInfo.metric.name]),
                    };
                });

                fieldsGroups[displayableMetrics.metricDefinition.metricsDefinitionId] = fields;
            });

            return fieldsGroups;
        }

        return {};
    }, [displayableMetricsPerMetricDef, hiddenMetricsByMetricDefId, trans]);



    /**
     * Create and cache the props for EntityMetricsValuesViewComp
     */
    const entityMetricsValuesViewCompProps = useMemo((): EntityMetricsValuesViewCompProps => {
        // Each metrics definition data is a collection and has its own latest timestamp. We will use the latest one by getting all the timestamps
        // into an array and sort them and then use the last timestamp.
        const sortedTimestamps = (displayableMetricsPerMetricDef ?? []).map((displayableMetric) => {
            return displayableMetric.latestDataTimestamp;
        }).sort();
        const latestDataTimestamp = sortedTimestamps.length > 0 ? sortedTimestamps[sortedTimestamps.length - 1] : undefined;

        return {
            className: clsx(props.className, 'match-height', isEditing && 'editing'),
            isEditing,
            title    : selectedEntity?.displaySettings.name,
            timestamp: latestDataTimestamp
                ? getDateTimeDisplay((new Date(latestDataTimestamp)).toISOString(), trans)
                : trans('label.none'),
            fields                : fieldSettings,
            customSecondaryActions: [...(props.onClick ? [{
                label  : capitalizeAll(trans('entity.action.show_entity_details', { term: trans('terms.entity') })),
                icon   : 'info',
                onClick: props.onClick,
            }] : []), {
                label  : capitalizeAll(trans('label.edit')),
                icon   : 'edit',
                onClick: () => {
                    onChangeEditingMode(true);
                },
            }],
            actions: {
                cancelEdit: {
                    label  : capitalizeAll(trans('label.cancel')),
                    onClick: closeEditing,
                },
                saveUpdate: {
                    label  : capitalizeAll(trans('label.save')),
                    onClick: onFormSubmit,
                },
            },
            text: {
                updatedDate: capitalizeAll(trans('label.updated_date')),
            },
            noMetricsMessageComp: (
                <GenericMessageBox
                    title={trans('generic.no_data.title', {
                        term: capitalizeAll(trans('label.metric', { count: 2 })),
                    })}
                />
            ),
        };
    }, [closeEditing, displayableMetricsPerMetricDef, fieldSettings, isEditing, onChangeEditingMode, onFormSubmit, props.className, props.onClick,
        selectedEntity?.displaySettings.name, trans]);


    return (
        <EntityMetricsValuesViewComp {...entityMetricsValuesViewCompProps} />
    );
};



// Register this component with the RitzComponentsCollection so that it can be used with the Ritz UI customization system
RitzComponentsCollection.getInstance().registerComponent(
    'EntityLatestMetricValueViewUIComponent', EntityLatestMetricValueViewUIComponent,
);



// Register the component with the EntityUIComponentCollection so that the component can be used by the Entity Layout System
EntityUIComponentCollection.getInstance().registerComponent(
    'Entity_Latest_Metrics_Value_Comp',
    {
        componentFunc          : EntityLatestMetricValueViewUIComponent,
        customSettingsValidator: isEntityLatestMetricValueViewUIComponentCustomSettings,
    },
);
